import { EVENTS } from '@bs/constants';

/**
 * The DonutChart module which handles animated donut chart visualization.
 * @class DonutChart
 */
export default class DonutChart {
  /**
   * DonutChart constructor which assigns the element passed into the constructor
   * to the `this.element` property for later reference
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * Reference to the DOM element that is the root of the component
     * @property {object} element - The DOM element that serves as the root of the component.
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} DonutChart A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} DonutChart A reference to the instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.circle = this.element.querySelector('.circle');
    this.percentage = this.element.querySelector('.percentage');

    // Check if required elements exist
    if (!this.circle || !this.percentage) {
      return this;
    }

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.animateChart = this.animateChart.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} DonutChart A reference to the instance of the class
   * @chainable
   */
  enable() {
    // Only start animation if required elements exist
    if (this.circle && this.percentage) {
      this.animateChart();
    }

    return this;
  }

  /**
   * Animate the donut chart with circle stroke and percentage counter
   * @returns {object} A reference to the current instance of the class
   */
  animateChart() {
    if (!this.circle || !this.percentage) return this;

    const dataValue = this.circle.getAttribute('data-value');
    if (!dataValue) {
      return this;
    }

    const targetValue = parseInt(dataValue, 10);
    if (Number.isNaN(targetValue) || targetValue < 0 || targetValue > 100) {
      return this;
    }

    const circumference = 2 * Math.PI * 70;
    const offset = circumference - (targetValue / 100) * circumference;

    // Animate circle
    setTimeout(() => {
      this.circle.style.strokeDashoffset = offset;
    }, 100);

    // Animate percentage counter
    let currentValue = 0;
    const increment = targetValue / 60;

    const counter = setInterval(() => {
      currentValue += increment;
      if (currentValue >= targetValue) {
        currentValue = targetValue;
        clearInterval(counter);
      }
      this.percentage.textContent = `${Math.round(currentValue)}%`;
    }, 33);

    return this;
  }
}

/**
 * Initialize all donut charts on the page
 */
export function initDonutCharts() {
  const charts = document.querySelectorAll('.chart__container');

  if (!charts.length) return;

  const chartObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // eslint-disable-next-line no-new
          new DonutChart(entry.target);
          observer.unobserve(entry.target); // Run once per chart
        }
      });
    },
    { threshold: 0.3 }
  );

  charts.forEach((chart) => chartObserver.observe(chart));
}

// Auto-initialize when DOM is ready
document.addEventListener(EVENTS.DOM_CONTENT_LOADED, initDonutCharts);
